import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  X, Send, Loader2, Zap, Clock, Sparkles, Edit2, Check, RefreshCw, AlertTriangle
} from "lucide-react";

const DRIP_OPTIONS = [
  { value: "instant", label: "Now", icon: Zap },
  { value: "1hour", label: "1 Hour", icon: Clock },
  { value: "1day", label: "1 Day", icon: Clock },
  { value: "1week", label: "1 Week", icon: Clock },
];

export default function CommentComposer({ entries = [], onClose, onSent }) {
  const { api } = useAuth();
  const [step, setStep] = useState("config"); // config → preview → result
  const [count, setCount] = useState(3);
  const [dripFeed, setDripFeed] = useState("1hour");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [comments, setComments] = useState([]); // Array of { text, editing }
  const [editIdx, setEditIdx] = useState(null);
  const [editText, setEditText] = useState("");
  const [result, setResult] = useState(null);
  const [projectContext, setProjectContext] = useState({ url: "", context: "" });

  // Fetch project settings for website context — match entry's project
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/projects");
        const projects = res.data || [];
        const entryProjectId = entries[0]?.project_id;
        // Find the matching project, fall back to first
        const p = projects.find(pr => pr.id === entryProjectId) || projects[0];
        if (p) {
          setProjectContext({
            url: p.website_url || p.website || "",
            context: p.website_context || p.description || "",
            name: p.name || "",
          });
        }
      } catch {}
    })();
  }, [api, entries]);

  const selectedEntry = entries[0] || {};

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/llm/generate-comments-batch", {
        title: selectedEntry.title || selectedEntry.url || "",
        body: selectedEntry.body || "",
        subreddit: selectedEntry.subreddit || "",
        url: selectedEntry.url || "",
        website_context: projectContext.context,
        website_url: projectContext.url,
        count: count,
      });
      const generated = res.data.comments || [];
      setComments(generated.map(text => ({ text, editing: false })));
      setStep("preview");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate comments. Check Grok API key.");
    } finally {
      setGenerating(false);
    }
  };

  const handleEditStart = (idx) => {
    setEditIdx(idx);
    setEditText(comments[idx].text);
  };

  const handleEditSave = (idx) => {
    const updated = [...comments];
    updated[idx] = { ...updated[idx], text: editText };
    setComments(updated);
    setEditIdx(null);
  };

  const handleRemoveComment = (idx) => {
    setComments(comments.filter((_, i) => i !== idx));
  };

  const handleRegenerate = async (idx) => {
    setGenerating(true);
    try {
      const res = await api.post("/llm/generate-comment", {
        title: selectedEntry.title || selectedEntry.url || "",
        body: selectedEntry.body || "",
        subreddit: selectedEntry.subreddit || "",
        website_context: projectContext.context,
        website_url: projectContext.url,
      });
      const updated = [...comments];
      updated[idx] = { text: res.data.comment, editing: false };
      setComments(updated);
    } catch (err) {
      alert("Regeneration failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    const texts = comments.map(c => c.text).filter(t => t.trim());
    if (texts.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      // Submit each comment via buyupvotes.io
      let submitted = 0;
      let failed = 0;
      const errors = [];
      for (const text of texts) {
        try {
          await api.post("/buyupvotes/comment", {
            entry_id: entries[0]?.id,
            content: text,
          });
          submitted++;
        } catch (err) {
          failed++;
          errors.push(err.response?.data?.detail || "Failed");
        }
      }
      setResult({
        status: failed === 0 ? "success" : submitted > 0 ? "partial" : "error",
        message: `${submitted} comment(s) submitted to buyupvotes.io${failed > 0 ? `, ${failed} failed` : ""}`,
        unmapped: errors,
      });
      setStep("result");
      if (onSent) onSent();
    } catch (err) {
      setResult({ status: "error", message: err.response?.data?.detail || "Failed to submit comments" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose} data-testid="comment-composer-overlay">
      <div onClick={e => e.stopPropagation()} style={{
        background: "#111113", border: "1px solid #27272a", borderRadius: 12,
        width: "100%", maxWidth: 580, maxHeight: "85vh", overflow: "auto",
        boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
      }} data-testid="comment-composer-modal">
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid #1e1e1e", position: "sticky", top: 0,
          background: "#111113", zIndex: 1,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={16} color="#a78bfa" />
            <span style={{ fontSize: 15, fontWeight: 700, color: "#e5e5e5" }}>
              {step === "config" ? "Comment Composer" : step === "preview" ? "Review Comments" : "Done"}
            </span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }} data-testid="comment-composer-close">
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "20px" }}>
          {/* Target info */}
          <div style={{
            padding: "10px 14px", background: "#18181b", borderRadius: 8, marginBottom: 16,
            border: "1px solid #27272a", fontSize: 12, color: "#9ca3af",
          }}>
            <div style={{ fontWeight: 600, color: "#e5e5e5", marginBottom: 2 }}>
              Target: {entries.length} entr{entries.length === 1 ? "y" : "ies"}
            </div>
            {selectedEntry.subreddit && <span>r/{selectedEntry.subreddit}</span>}
            {selectedEntry.url && (
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2, color: "#6b7280", fontSize: 11 }}>
                {selectedEntry.url}
              </div>
            )}
          </div>

          {/* Step 1: Config */}
          {step === "config" && (
            <>
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>
                  Number of comments (each from a different profile):
                </label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[1, 2, 3, 5, 8].map(n => (
                    <button
                      key={n}
                      onClick={() => setCount(n)}
                      data-testid={`comment-count-${n}`}
                      style={{
                        padding: "6px 14px", fontSize: 13, borderRadius: 6, cursor: "pointer",
                        border: `1px solid ${count === n ? "#a78bfa" : "#27272a"}`,
                        background: count === n ? "#2d1f4e" : "#111113",
                        color: count === n ? "#a78bfa" : "#6b7280",
                        fontWeight: count === n ? 700 : 400,
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>
                  Spread comments over:
                </label>
                <div style={{ display: "flex", gap: 6 }} data-testid="comment-drip-options">
                  {DRIP_OPTIONS.map(opt => {
                    const active = dripFeed === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setDripFeed(opt.value)}
                        data-testid={`comment-drip-${opt.value}`}
                        style={{
                          padding: "6px 14px", fontSize: 12, borderRadius: 6, cursor: "pointer",
                          border: `1px solid ${active ? "#3b82f6" : "#27272a"}`,
                          background: active ? "#1e3a5f" : "#111113",
                          color: active ? "#60a5fa" : "#6b7280",
                          display: "flex", alignItems: "center", gap: 5, fontWeight: active ? 600 : 400,
                        }}
                      >
                        <opt.icon size={12} /> {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating}
                data-testid="generate-comments-btn"
                style={{
                  width: "100%", padding: "12px", borderRadius: 8, cursor: "pointer",
                  background: generating ? "#27272a" : "linear-gradient(135deg, #7c3aed, #a78bfa)",
                  color: "#fff", fontWeight: 700, fontSize: 14, border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {generating ? "Grok is writing..." : `Generate ${count} Comments with Grok`}
              </button>
            </>
          )}

          {/* Step 2: Preview & Edit */}
          {step === "preview" && (
            <>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                Review, edit, or regenerate each comment before posting:
              </div>
              {comments.map((c, idx) => (
                <div key={idx} style={{
                  marginBottom: 10, border: "1px solid #27272a", borderRadius: 8,
                  background: "#18181b", overflow: "hidden",
                }} data-testid={`comment-preview-${idx}`}>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 12px", borderBottom: "1px solid #27272a", background: "#1a1a1a",
                  }}>
                    <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>
                      Comment #{idx + 1}
                    </span>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        onClick={() => handleRegenerate(idx)}
                        disabled={generating}
                        title="Regenerate"
                        style={{ background: "none", border: "1px solid #27272a", borderRadius: 4, padding: "2px 6px", cursor: "pointer", color: "#6b7280", fontSize: 11, display: "flex", alignItems: "center", gap: 3 }}
                      >
                        <RefreshCw size={10} /> Regen
                      </button>
                      {editIdx === idx ? (
                        <button
                          onClick={() => handleEditSave(idx)}
                          style={{ background: "none", border: "1px solid #10b98144", borderRadius: 4, padding: "2px 6px", cursor: "pointer", color: "#10b981", fontSize: 11, display: "flex", alignItems: "center", gap: 3 }}
                        >
                          <Check size={10} /> Save
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditStart(idx)}
                          style={{ background: "none", border: "1px solid #27272a", borderRadius: 4, padding: "2px 6px", cursor: "pointer", color: "#6b7280", fontSize: 11, display: "flex", alignItems: "center", gap: 3 }}
                        >
                          <Edit2 size={10} /> Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveComment(idx)}
                        style={{ background: "none", border: "1px solid #ef444433", borderRadius: 4, padding: "2px 6px", cursor: "pointer", color: "#ef4444", fontSize: 11 }}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  </div>
                  {editIdx === idx ? (
                    <textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      data-testid={`comment-edit-textarea-${idx}`}
                      style={{
                        width: "100%", minHeight: 80, padding: 12, background: "#111113", color: "#e5e5e5",
                        border: "none", resize: "vertical", fontSize: 13, lineHeight: 1.5, fontFamily: "inherit",
                        outline: "none",
                      }}
                    />
                  ) : (
                    <div style={{ padding: 12, fontSize: 13, color: "#d4d4d4", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                      {c.text}
                    </div>
                  )}
                </div>
              ))}

              {comments.length === 0 && (
                <div style={{ textAlign: "center", padding: 20, color: "#6b7280" }}>
                  All comments removed. Go back to regenerate.
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button
                  onClick={() => setStep("config")}
                  style={{
                    flex: 1, padding: "10px", borderRadius: 8, cursor: "pointer",
                    background: "#18181b", color: "#9ca3af", border: "1px solid #27272a",
                    fontSize: 13, fontWeight: 600,
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={sending || comments.length === 0}
                  data-testid="submit-comments-btn"
                  style={{
                    flex: 2, padding: "10px", borderRadius: 8, cursor: "pointer",
                    background: sending ? "#27272a" : "#a78bfa", color: "#fff", fontWeight: 700,
                    fontSize: 14, border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {sending ? "Submitting..." : `Submit ${comments.length} Comments via buyupvotes.io`}
                </button>
              </div>
            </>
          )}

          {/* Step 3: Result */}
          {step === "result" && result && (
            <div style={{
              padding: 16, borderRadius: 8, fontSize: 13,
              background: result.status === "success" ? "#0f291a" : "#2a0f0f",
              border: `1px solid ${result.status === "success" ? "#10b98144" : "#ef444444"}`,
              color: result.status === "success" ? "#10b981" : "#ef4444",
            }} data-testid="comment-result">
              {result.message}
              {result.unmapped?.length > 0 && (
                <div style={{ marginTop: 6, color: "#f59e0b", fontSize: 11 }}>
                  <AlertTriangle size={12} style={{ marginRight: 4 }} />
                  Unmapped accounts (no action): {result.unmapped.join(", ")}
                </div>
              )}
              <button
                onClick={onClose}
                style={{
                  marginTop: 12, padding: "8px 16px", borderRadius: 6, cursor: "pointer",
                  background: "#27272a", color: "#e5e5e5", border: "none", fontSize: 13,
                }}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
